import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { CashIsleLogo } from "@/components/CashIsleLogo";
import { Footer } from "@/components/Footer";
import { ArrowRight, Zap, ShieldCheck, Sparkles } from "lucide-react";

const Landing = () => {
  return (
    <main className="min-h-screen bg-wave-gradient text-primary-foreground overflow-hidden relative flex flex-col">
      <Helmet>
        <title>Cash Isle — Caribbean money, reimagined</title>
        <meta name="description" content="Send, receive and grow money across the islands. Cash Isle is the Bahamas' financial super app — wallet, gigs with escrow, and instant payouts." />
        <link rel="canonical" href="https://cash-isle-hustle-hub.lovable.app/" />
        <meta property="og:title" content="Cash Isle — Caribbean money, reimagined" />
        <meta property="og:description" content="The Bahamas' financial super app — wallet, gigs with escrow, and instant payouts." />
        <meta property="og:url" content="https://cash-isle-hustle-hub.lovable.app/" />
      </Helmet>

      {/* Decorative blobs */}
      <div className="absolute top-10 -left-24 w-80 h-80 rounded-full bg-primary-glow/30 blur-3xl" aria-hidden />
      <div className="absolute -bottom-24 -right-20 w-[420px] h-[420px] rounded-full bg-accent/25 blur-3xl" aria-hidden />

      {/* Top nav */}
      <nav className="relative z-10 max-w-5xl w-full mx-auto px-5 py-5 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2" aria-label="Cash Isle home">
          <CashIsleLogo size={36} />
          <span className="font-display font-bold text-lg">Cash Isle</span>
        </Link>

        <ul className="hidden md:flex items-center gap-7 text-sm font-medium">
          <li><a href="#features" className="opacity-90 hover:opacity-100 transition-opacity">Features</a></li>
          <li><Link to="/faq" className="opacity-90 hover:opacity-100 transition-opacity">FAQ</Link></li>
          <li><Link to="/support" className="opacity-90 hover:opacity-100 transition-opacity">Support</Link></li>
          <li><Link to="/waitlist" className="opacity-90 hover:opacity-100 transition-opacity">Waitlist</Link></li>
        </ul>

        <div className="flex items-center gap-3">
          <Link to="/auth" className="text-sm font-semibold opacity-90 hover:opacity-100">
            Sign in
          </Link>
          <Link to="/auth?signup=1" className="hidden sm:inline-block">
            <Button variant="gold" size="sm">Get $5</Button>
          </Link>
        </div>
      </nav>


      {/* Hero */}
      <section className="relative z-10 flex-1 flex flex-col px-6 pt-6 pb-8 max-w-md w-full mx-auto">
        <div className="animate-fade-in-up">
          <span className="inline-flex items-center gap-2 bg-accent/25 backdrop-blur px-3 py-1 rounded-full text-xs font-semibold mb-5">
            <Sparkles className="h-3.5 w-3.5 text-accent" /> Limited welcome offer
          </span>
          <h1 className="font-display font-extrabold text-[2.75rem] sm:text-5xl leading-[1.05] tracking-tight">
            Get <span className="text-accent">$5.</span><br />
            Send money instantly.
          </h1>
          <p className="text-base text-primary-foreground/85 mt-5 max-w-sm">
            No cash. No waiting. Works anywhere in The Bahamas.
          </p>
        </div>

        {/* Animated wallet preview */}
        <div className="relative mt-8 mx-auto w-full max-w-[280px] animate-scale-in">
          <div className="absolute -inset-6 bg-accent/30 rounded-[2.5rem] blur-2xl" aria-hidden />
          <div className="relative rounded-[2rem] bg-background text-foreground shadow-card border-[6px] border-primary-deep/80 overflow-hidden">
            <div className="bg-card-gradient text-primary-foreground px-5 pt-6 pb-7 rounded-b-3xl">
              <p className="text-xs uppercase tracking-wider opacity-70">Available</p>
              <p className="font-display text-4xl font-extrabold mt-1 flex items-baseline gap-1">
                $5.00
                <span className="text-xs font-medium text-accent ml-2 animate-pulse">+ unlocked</span>
              </p>
            </div>
            <div className="p-4 space-y-2">
              <PreviewRow label="Send to @marcus" amount="-$3.00" tone="out" />
              <PreviewRow label="From @kayla" amount="+$8.00" tone="in" />
              <PreviewRow label="Send to @junkanoo" amount="-$2.50" tone="out" />
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 space-y-3">
          <Link to="/auth?signup=1" className="block">
            <Button variant="gold" size="xl" className="w-full gap-2">
              Get $5 & Start <ArrowRight className="h-5 w-5" />
            </Button>
          </Link>

          <Link to="/waitlist" className="block">
            <Button variant="outline" size="lg" className="w-full border-white/30 bg-white/10 text-primary-foreground hover:bg-white/20 hover:text-primary-foreground">
              Join the waitlist
            </Button>
          </Link>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <Trust icon={Zap} label="Instant sends" />
            <Trust icon={ShieldCheck} label="PIN protected" />
          </div>

          <p className="text-[11px] text-center text-primary-foreground/60 pt-2">
            $5 unlocks after your first send. One reward per device.
          </p>
        </div>
      </section>

      <Footer />
    </main>
  );
};

const PreviewRow = ({
  label,
  amount,
  tone,
}: {
  label: string;
  amount: string;
  tone: "in" | "out";
}) => (
  <div className="flex items-center justify-between bg-secondary/70 rounded-xl px-3 py-2.5">
    <span className="text-sm font-medium truncate">{label}</span>
    <span
      className={`font-display font-bold text-sm ${
        tone === "in" ? "text-success" : "text-foreground"
      }`}
    >
      {amount}
    </span>
  </div>
);

const Trust = ({
  icon: Icon,
  label,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
}) => (
  <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-xl px-3 py-2.5 border border-white/15">
    <Icon className="h-4 w-4 text-accent shrink-0" />
    <span className="text-xs font-medium">{label}</span>
  </div>
);

export default Landing;
